import React, { useEffect, memo } from 'react';
import { Box, TextField, FormControlLabel, Checkbox, CircularProgress } from '@mui/material';
import { StyledDropdown } from '../../../../core/components/ui/StyledDropdown';
import SectionTitle from '../../../../core/components/ui/SectionTitle';
import FormDatePicker from '../../../../core/components/ui/FormDatePicker';
import InfoBox from '../../../../core/components/ui/InfoBox';
import { SelectChangeEvent } from '@mui/material/Select';
import { AddressSection } from './AddressSection';
import { useFormContext, Controller, useWatch } from 'react-hook-form';
import { useMemberDemographicsQuery } from '../../../../core/queries/useDemographicsQuery';
import { useSelector } from 'react-redux';
import {
  selectEthnicityDropdownOptions,
  selectGenderDropdownOptions,
  selectPronounDropdownOptions,
  selectSexualOrientationDropdownOptions,
} from '../../../../core/store/slices/demographic.slice';
import { RootState } from '../../../../core/store';

interface PersonalDetailsStepProps {
  isSaving?: boolean;
}

const PersonalDetailsStep: React.FC<PersonalDetailsStepProps> = ({ isSaving = false }) => {
  const personalDetails = useSelector((state: RootState) => state.personalDetails);
  const {
    control,
    formState: { errors },
    reset,
    setValue,
    watch
  } = useFormContext();

  const { isLoading: isDemographicsLoading } = useMemberDemographicsQuery();

  const genderOptions = useSelector(selectGenderDropdownOptions);
  const pronounOptions = useSelector(selectPronounDropdownOptions);
  const ethnicityOptions = useSelector(selectEthnicityDropdownOptions);
  const sexualOrientationOptions = useSelector(selectSexualOrientationDropdownOptions);
  const isStudent = watch('isStudent');
  const school = useWatch({ control, name: 'school' });

  useEffect(() => {
    if (!personalDetails || isDemographicsLoading) return;

    let isInitialRender = true;

    if (isInitialRender) {

      reset(
        {
          firstName: personalDetails.firstName || '',
          lastName: personalDetails.lastName || '',
          preferredName: personalDetails.preferredName,
          email: personalDetails.email || '',
          dob: personalDetails.dob || '',
          races: personalDetails.races || [],
          sexualOrientation: personalDetails.sexualOrientation || '',
          pronounsList: personalDetails.pronounsList || [],
          gender: personalDetails.gender || '',
          school: personalDetails.school || '',
          address: personalDetails.address || '',
          state: personalDetails.state || '',
          city: personalDetails.city || '',
          zip: personalDetails.zip || '',
          // preferredLanguage: personalDetails.preferredLanguage || ''
        },
        { keepDefaultValues: true }
      );
      isInitialRender = false;
    }
  }, [
    personalDetails?.firstName,
    personalDetails?.lastName,
    personalDetails?.preferredName,
    personalDetails?.email,
    personalDetails?.dob,
    personalDetails?.races,
    personalDetails?.sexualOrientation,
    personalDetails?.pronounsList,
    personalDetails.gender,
    personalDetails?.school,
    personalDetails?.address,
    personalDetails?.state,
    personalDetails?.city,
    personalDetails?.zip,
    // personalDetails?.preferredLanguage,
    isDemographicsLoading,
    reset,
  ]);

  useEffect(() => {
    const hasSchoolValue = !!school?.trim();
    setValue('isStudent', hasSchoolValue);
  }, [school, setValue]);

  return (
    <Box>
      <Box sx={{ pt: 2, mb: 4 }}>
        <SectionTitle>Personal Details</SectionTitle>
        <Controller
          name='firstName'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='First Name'
              {...field}
              value={field.value || ''}
              variant='outlined'
              error={Boolean(errors.firstName)}
              required
              sx={{ mb: 3 }}
              disabled={isSaving}
            />
          )}
        />

        <Controller
          name='lastName'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='Last Name'
              {...field}
              value={field.value || ''}
              variant='outlined'
              error={Boolean(errors.lastName)}
              required
              sx={{ mb: 3 }}
              disabled={isSaving}
            />
          )}
        />

        <Controller
          name='preferredName'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='Preferred Name'
              {...field}
              value={field.value || ''}
              variant='outlined'
              helperText='What would you like to be called if different from your first name'
              sx={{ mb: 3 }}
              disabled={isSaving}
            />
          )}
        />

        <Controller
          name='email'
          control={control}
          render={({ field }) => (
            <TextField
              fullWidth
              label='Email Address'
              type='email'
              {...field}
              value={field.value || ''}
              variant='outlined'
              error={Boolean(errors.email)}
              required
              sx={{ mb: 3 }}
              disabled={isSaving}
            />
          )}
        />

        <Controller
          name='dob'
          control={control}
          render={({ field }) => (
            <FormDatePicker
              name='personal-details-dob'
              label='Date of Birth'
              value={field.value}
              onChange={field.onChange}
              maxWidth="50%"
              disableFuture={true}
              placement='top'
              required={true}
              error={Boolean(errors.dob)}
            />
          )}
        />
      </Box>
      <AddressSection isSaving={isSaving} />

      <Box sx={{ pt: 2, mb: 4 }}>
        <SectionTitle>Demographics (Optional)</SectionTitle>
        <Controller
          name='races'
          control={control}
          render={({ field }) => (
            <StyledDropdown
              label='What is your ethnicity'
              name={field.name}
              value={field.value || []}
              multiple={true}
              onChange={(e: SelectChangeEvent<string | string[]>) => {
                field.onChange(e.target.value);
              }}
              options={ethnicityOptions}
              data-id='races-multiple-selection-dropdown'
              sx={{ mb: 3 }}
              disabled={isSaving}
              startAdornment={
                isDemographicsLoading ? (
                  <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                ) : undefined
              }
            />
          )}
        />

        <Controller
          name='sexualOrientation'
          control={control}
          render={({ field }) => (
            <StyledDropdown
              label='What is your sexual orientation'
              name={field.name}
              value={field.value || ''}
              onChange={(e: SelectChangeEvent<string | string[]>) => {
                field.onChange(e.target.value);
              }}
              options={sexualOrientationOptions}
              data-id='sexual-orientation-dropdown'
              sx={{ mb: 3 }}
              disabled={isSaving || isDemographicsLoading}
              startAdornment={
                isDemographicsLoading ? (
                  <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                ) : undefined
              }
            />
          )}
        />

        <Controller
          name='gender'
          control={control}
          render={({ field }) => (
            <StyledDropdown
              label='What is your gender'
              name={field.name}
              value={field.value || ''}
              onChange={(e: SelectChangeEvent<string | string[]>) => {
                field.onChange(e.target.value);
              }}
              options={genderOptions}
              data-id='gender-dropdown'
              sx={{ mb: 3 }}
              disabled={isSaving || isDemographicsLoading}
              startAdornment={
                isDemographicsLoading ? (
                  <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                ) : undefined
              }
            />
          )}
        />

        <Controller
          name='pronounsList'
          control={control}
          render={({ field }) => (
            <StyledDropdown
              label='What are your pronouns?'
              name={field.name}
              value={field.value || []}
              multiple={true}
              onChange={(e: SelectChangeEvent<string | string[]>) => {
                field.onChange(e.target.value);
              }}
              options={pronounOptions}
              data-id='pronouns-multiple-selection-dropdown'
              sx={{ mb: 3 }}
              disabled={isSaving}
              startAdornment={
                isDemographicsLoading ? (
                  <CircularProgress size={20} color='inherit' sx={{ mr: 1 }} />
                ) : undefined
              }
            />
          )}
        />
      </Box>



      <Box sx={{ pt: 2, mb: 1 }}>
        <SectionTitle>Additional Information</SectionTitle>

        {/* <Box sx={{ mt: 2 }}>
          <Controller
            name='preferredLanguage'
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label='Preferred Language'
                {...field}
                value={field.value || ''}
                variant='outlined'
                disabled={isSaving}
              />
            )}
          />
        </Box> */}
        <Controller
          name='isStudent'
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  disabled={isSaving}
                />
              }
              label='I am currently a student'
            />
          )}
        />


        {isStudent && (
          <Controller
            name='school'
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label='School Name'
                {...field}
                value={field.value || ''}
                variant='outlined'
                sx={{ mt: 2 }}
                disabled={isSaving}
                error={Boolean(errors.school)}
                required
              />
            )}
          />
        )}
      </Box>

      <InfoBox sx={{ mt: 4 }}>
        Your information helps us provide better support to you. All demographic information is
        optional and confidential.
      </InfoBox>
    </Box>
  );
};

export default memo(PersonalDetailsStep);
